import React, { useState, useEffect } from 'react';

export default function Leaderboard({ backendUrl, user, language }) {
  const [selectedTest, setSelectedTest] = useState('vertical_jump');
  const [period, setPeriod] = useState('all');
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(false);

  const tests = [
    { id: 'vertical_jump', name: 'Vertical Jump', emoji: '🏀', unit: 'm' },
    { id: 'situps', name: 'Sit-ups', emoji: '💪', unit: 'reps' },
    { id: 'shuttle_run', name: 'Shuttle Run', emoji: '🏃', unit: 's' }
  ];

  const periods = [
    { id: 'today', name: 'Today' },
    { id: 'week', name: 'This Week' },
    { id: 'month', name: 'This Month' },
    { id: 'all', name: 'All Time' }
  ];

  useEffect(() => {
    fetchLeaderboard();
    fetchUserRank();
  }, [selectedTest, period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/leaderboard/${selectedTest}?period=${period}`);
      const data = await res.json();
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error('Fetch leaderboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRank = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${backendUrl}/api/leaderboard/user/${user.id}/rank?test=${selectedTest}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await res.json();
      if (data.success && data.hasSubmission) {
        setUserRank(data);
      } else {
        setUserRank(null);
      }
    } catch (error) {
      console.error('Fetch user rank error:', error);
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl p-8 text-white shadow-xl">
        <h1 className="text-4xl font-bold mb-2">🏆 Leaderboard</h1>
        <p className="text-white/90">Compete with athletes around the world</p>
      </div>

      {/* User Rank Card */}
      {userRank && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">Your Rank</h3>
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold">{getMedalEmoji(userRank.rank)}</div>
                <div>
                  <div className="text-3xl font-bold">{userRank.score}</div>
                  <div className="text-sm text-blue-100">
                    Top {userRank.percentile}% • {userRank.totalParticipants} participants
                  </div>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-6xl font-bold opacity-20">{userRank.rank}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Test Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Test
            </label>
            <div className="grid grid-cols-3 gap-2">
              {tests.map(test => (
                <button
                  key={test.id}
                  onClick={() => setSelectedTest(test.id)}
                  className={`p-3 rounded-lg text-center transition ${
                    selectedTest === test.id
                      ? 'bg-blue-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="text-2xl mb-1">{test.emoji}</div>
                  <div className="text-xs font-medium">{test.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Period Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Period
            </label>
            <div className="grid grid-cols-4 gap-2">
              {periods.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition ${
                    period === p.id
                      ? 'bg-green-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading leaderboard...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <p className="text-gray-600 dark:text-gray-400">No submissions yet for this period</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {leaderboard.map((entry, index) => {
                  const isCurrentUser = entry.userId === user.id;
                  return (
                    <tr
                      key={entry.submissionId}
                      className={`${
                        isCurrentUser
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : index % 2 === 0
                          ? 'bg-white dark:bg-gray-800'
                          : 'bg-gray-50 dark:bg-gray-900'
                      } hover:bg-gray-100 dark:hover:bg-gray-700 transition`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{getMedalEmoji(entry.rank)}</span>
                          {isCurrentUser && (
                            <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                              You
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {entry.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {entry.organization || 'Independent'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                          {entry.score}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600 dark:text-gray-400">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-6 text-white shadow-lg">
          <div className="text-4xl mb-2">🥇</div>
          <div className="text-2xl font-bold mb-1">
            {leaderboard[0]?.score || 'N/A'}
          </div>
          <div className="text-sm text-white/80">Top Score</div>
          <div className="text-xs mt-2">{leaderboard[0]?.name || 'No entries'}</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
          <div className="text-4xl mb-2">📊</div>
          <div className="text-2xl font-bold mb-1">
            {leaderboard.length}
          </div>
          <div className="text-sm text-white/80">Total Participants</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
          <div className="text-4xl mb-2">📈</div>
          <div className="text-2xl font-bold mb-1">
            {leaderboard.length > 0
              ? Math.round(
                  leaderboard.reduce((sum, e) => sum + e.score, 0) / leaderboard.length
                )
              : 'N/A'}
          </div>
          <div className="text-sm text-white/80">Average Score</div>
        </div>
      </div>

      {/* Share Button */}
      {userRank && (
        <div className="text-center">
          <button
            onClick={() => {
              const text = `I ranked #${userRank.rank} out of ${userRank.totalParticipants} on ${selectedTest.replace('_', ' ')} with a score of ${userRank.score}! 🏆`;
              if (navigator.share) {
                navigator.share({ title: 'My Achievement', text });
              } else {
                navigator.clipboard.writeText(text);
                alert('Achievement copied to clipboard!');
              }
            }}
            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg font-semibold hover:from-pink-600 hover:to-rose-700 transition shadow-lg"
          >
            📢 Share My Achievement
          </button>
        </div>
      )}
    </div>
  );
}